import { ReactComponent as GleapBotIcon } from "../../Assets/GleapBotIcon.svg";
import { hasNamedAuthor, presentsAsBot } from "../../Helper/MessageAuthor";
import { getMessageAuthorName } from "../../Helper/MessageAuthorName";
import { useConfigStore } from "../../Stores/Config";
import { TicketChatMessage } from "../../Stores/Ticket";
import { useKaiAgentState } from "../../Helper/useKaiAgentState";
import { AvatarImage } from "../AvatarImage/AvatarImage";
import { KaiAgentStatus } from "../KaiAgentStatus/KaiAgentStatus";
import "./ChatMessageAuthor.scss";

// Avatar + name shown above the FIRST message of every agent-side group. The
// timeline groups consecutive messages from one author, so this header appears
// exactly when the sender changes — without it a reply from a second agent
// looked like a continuation of the first one's message (#142998).
export const ChatMessageAuthor = ({
  message,
  live = false,
  typing = false,
  settled = false,
}: {
  message: TicketChatMessage;
  /** The row follows Kai's live state (typing placeholder, streaming bubble, latest answer). */
  live?: boolean;
  /** The row is a typing / pending-thinking placeholder. */
  typing?: boolean;
  /** The row is the settled latest answer: attention / paused / done only. */
  settled?: boolean;
}) => {
  const operatorAvatarImage = useConfigStore(
    (state) => state.config.operatorAvatarImage,
  );
  const ticketHeaderMode = useConfigStore(
    (state) => state.config.ticketHeaderMode,
  );
  const kaiState = useKaiAgentState({ live, typing, settled });

  if (!hasNamedAuthor(message)) {
    return null;
  }

  const name = getMessageAuthorName(message);

  // The AI reads as a product, not a person: its mark sits in a rounded square
  // while teammates keep the circular avatar people expect. Under the
  // "ai-only" conversation header teammates are presented as the AI too, so
  // their own name and picture never surface.
  const isBot = presentsAsBot(message, ticketHeaderMode);

  // Same cascade the per-message avatar used before it was removed: the agent's
  // own picture, else the project's configured operator image, else the Gleap
  // bot mark. AvatarImage falls back to generated initials when a remote image
  // fails to load.
  const renderAvatar = () => {
    if (!isBot && message.user?.profileImageUrl) {
      return <AvatarImage src={message.user.profileImageUrl} alt={name} />;
    }

    // Per-message agent identity: an AI agent (e.g. Kai Resolve) ships its own
    // workspace-configured avatar on the comment, so a second agent tier is
    // visually distinct from the frontline bot.
    if (isBot && message.data?.agentAvatarUrl) {
      return (
        <AvatarImage
          src={message.data.agentAvatarUrl}
          alt={name}
          fallback={<GleapBotIcon />}
        />
      );
    }

    if (isBot && message.data?.agentName) {
      return (
        <AvatarImage
          src={operatorAvatarImage}
          alt={name}
          fallback={<GleapBotIcon />}
        />
      );
    }

    if (isBot && operatorAvatarImage && operatorAvatarImage.length > 0) {
      return (
        <AvatarImage
          src={operatorAvatarImage}
          alt={name}
          fallback={<GleapBotIcon />}
        />
      );
    }

    // A teammate without a profile picture still gets initials rather than the
    // bot mark, so the customer never sees a colleague branded as the AI.
    if (!isBot) {
      return <AvatarImage alt={name} />;
    }

    // No custom image anywhere: the animated Kai mark. Live rows show what
    // Kai is doing; history rows just blink.
    return <KaiAgentStatus state={kaiState} size={20} />;
  };

  return (
    <div className="chat-message-author">
      <span
        className={`chat-message-author-avatar ${
          isBot ? "chat-message-author-avatar--bot" : ""
        }`}
      >
        {renderAvatar()}
      </span>
      <span className="chat-message-author-name">{name}</span>
    </div>
  );
};

export default ChatMessageAuthor;
